import {
  Alert,
  Button,
  Card,
  Center,
  PasswordInput,
  PinInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
// zod4Resolver, not zodResolver: the latter reads `parsed.error.errors`, which
// zod 4 does not have, so validation fails silently and the form submits an
// invalid value. This project is on zod 4. Every form uses zod4Resolver.
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import { signIn, verifyTotp } from '../api/client.js';
import { useAuth } from '../auth/useAuth.js';
import { describeError } from '../components/ErrorMessage.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';

/**
 * Sign in, and the second factor when the account has one.
 *
 * Better Auth owns the credential check. On success it sets the session cookie
 * and the auth context exchanges that for a company token; this page never
 * sees either.
 *
 * The two steps are one page rather than two routes because the TOTP step is
 * only reachable directly after a password, and a `/login/totp` route would be
 * a URL a person could land on with nothing to verify.
 */

/*
  Built from `t` rather than defined at module scope, because the messages are
  translated and a module-level schema is frozen in whichever language happened
  to be loaded first. Every schema carrying user-facing text in this app is a
  function of `t` for the same reason.
*/
function credentialsSchema(t: (key: MessageKey) => string) {
  return z.object({
    email: z.email(t('login.emailInvalid')),
    password: z.string().min(1, t('login.passwordMissing')),
  });
}

type Credentials = z.infer<ReturnType<typeof credentialsSchema>>;

/**
 * A path within this application, or the dashboard.
 *
 * Anything that is not a single-slash-prefixed path is refused: `//evil.example`
 * and `https://evil.example` are both a browser's idea of somewhere else, and
 * either would turn "sign in and continue" into a redirect an attacker chose.
 */
function safeNext(value: unknown): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
    return '/';
  }
  return value;
}

export function LoginPage(): ReactNode {
  const t = useT();
  const { refresh, hasSession, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  /*
    Where the person was going before they were sent here.

    RequireAuth has always put this in the navigation state; nothing read it,
    so every sign-in landed on the dashboard regardless. It went unnoticed
    because arriving at the dashboard is a reasonable thing to happen. Browser
    login is where it stops being reasonable: a person following a connector's
    link to the consent screen has to come back to *that*, or the client is
    left waiting on a callback that never comes.

    Only same-site paths are honoured. `from` reaches this component through
    `history.state`, which a page on this origin can write, so treating it as a
    URL would make this an open redirect on the login page of an accounting
    system.
  */
  const next = safeNext((location.state as { from?: unknown } | null)?.from);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [needsTotp, setNeedsTotp] = useState(false);
  const [code, setCode] = useState('');

  const form = useForm<Credentials>({
    mode: 'uncontrolled',
    initialValues: { email: '', password: '' },
    validate: zod4Resolver(credentialsSchema(t)),
  });

  async function finishSignIn(): Promise<void> {
    /*
      A sign-in can succeed and leave no session behind.

      The credentials were accepted and the server set a cookie the browser then
      declined to keep or send — which is what happens when the page and the API
      are on different sites, because the session cookie is `SameSite=Lax`.
      Serving the UI from `localhost` while `VITE_API_BASE_URL` points at a LAN
      address does exactly that.

      Navigating anyway sends the user to a guarded route that bounces them
      straight back here, looking like a rejected password. So the condition is
      named instead.
    */
    if (!(await refresh())) {
      setError(t('login.sessionNotKept'));
      return;
    }
    await navigate(next, { replace: true });
  }

  async function handleCredentials(values: Credentials): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const result = await signIn(values.email, values.password);
      if (result.twoFactorRequired) {
        setNeedsTotp(true);
        return;
      }
      await finishSignIn();
    } catch (caught) {
      setError(describeError(caught, t));
    } finally {
      setBusy(false);
    }
  }

  async function handleTotp(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await verifyTotp(code);
      await finishSignIn();
    } catch (caught) {
      setError(describeError(caught, t));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  // Someone already signed in has no business on a login form: they would type
  // credentials they do not need, and a successful sign-in would land them
  // exactly where this redirect puts them now.
  if (!loading && hasSession) {
    return <Navigate to={next} replace />;
  }

  return (
    <Center mih="100dvh" p="md">
      <Card withBorder shadow="sm" padding="lg" w="100%" maw={400}>
        <Stack gap="md">
          <Title order={2}>{t('app.name')}</Title>

          {error !== null && (
            <Alert color="red" variant="light" role="alert">
              {error}
            </Alert>
          )}

          {needsTotp ? (
            <Stack gap="md">
              <Text size="sm">{t('login.totpPrompt')}</Text>
              <PinInput
                length={6}
                type="number"
                oneTimeCode
                value={code}
                onChange={setCode}
                aria-label={t('login.totpLabel')}
              />
              <Button
                fullWidth
                loading={busy}
                disabled={code.length !== 6}
                onClick={() => {
                  void handleTotp();
                }}
              >
                {t('login.totpSubmit')}
              </Button>
            </Stack>
          ) : (
            <form
              // Zod is the only validator. Without `noValidate` the browser's
              // own constraint validation on `type="email"` blocks the submit
              // event before Mantine sees it -- so the schema never runs, and
              // the user gets a browser tooltip in the browser's language
              // instead of the message written here.
              noValidate
              onSubmit={form.onSubmit((values) => {
                void handleCredentials(values);
              })}
            >
              <Stack gap="md">
                <TextInput
                  label={t('login.email')}
                  type="email"
                  autoComplete="username"
                  key={form.key('email')}
                  {...form.getInputProps('email')}
                />
                <PasswordInput
                  label={t('login.password')}
                  autoComplete="current-password"
                  key={form.key('password')}
                  {...form.getInputProps('password')}
                />
                <Button type="submit" fullWidth loading={busy}>
                  {t('login.submit')}
                </Button>
              </Stack>
            </form>
          )}
        </Stack>
      </Card>
    </Center>
  );
}
